import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface Integration {
  id: string
  displayName: string
  description?: string
  isEnabled: boolean
  baseUrl?: string
  syncFrequencyHours: number
  lastSyncAt?: string
  lastSyncStatus?: string
  lastSyncError?: string
}

export default function SetupIntegrations() {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null)
  const { getAccessToken } = useAuth()

  useEffect(() => {
    async function fetchIntegrations() {
      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()
        const data = await api.getSetupIntegrations(token)
        setIntegrations(data.integrations || [])
      } catch (err) {
        console.error('Failed to fetch integrations:', err)
        setError('Failed to load integrations.')
      } finally {
        setLoading(false)
      }
    }

    fetchIntegrations()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleEnabled = async (integrationId: string, currentEnabled: boolean) => {
    try {
      const token = await getAccessToken()
      const updated = await api.updateSetupIntegration(token, integrationId, {
        isEnabled: !currentEnabled,
      })
      setIntegrations(
        integrations.map((i) =>
          i.id === integrationId ? { ...i, ...updated.integration } : i
        )
      )
    } catch (err) {
      console.error('Failed to update integration:', err)
      setError('Failed to update integration.')
    }
  }

  const handleTestConnection = async (integrationId: string) => {
    try {
      setTestingId(integrationId)
      setTestResult(null)
      const token = await getAccessToken()
      const result = await api.testIntegrationConnection(token, integrationId)
      setTestResult({
        id: integrationId,
        success: result.success,
        message: result.message,
      })
    } catch (err) {
      console.error('Failed to test connection:', err)
      setTestResult({
        id: integrationId,
        success: false,
        message: 'Failed to test connection.',
      })
    } finally {
      setTestingId(null)
    }
  }

  const getStatusBadge = (integration: Integration) => {
    if (!integration.isEnabled) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Disabled
        </span>
      )
    }

    if (!integration.lastSyncStatus) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Not synced
        </span>
      )
    }

    if (integration.lastSyncStatus === 'Success') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Connected
        </span>
      )
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Error
      </span>
    )
  }

  const getIntegrationIcon = (id: string) => {
    const icons: Record<string, string> = {
      'partner-center': '☁️',
      'ninjaone': '🖥️',
      'huntress': '🛡️',
      'quickbooks': '💰',
      'screenconnect': '🔗',
    }
    return icons[id] || '📦'
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading integrations...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Sub-navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <Link
            to="/setup/customers"
            className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium"
          >
            Customers
          </Link>
          <Link
            to="/setup/integrations"
            className="border-primary-500 text-primary-600 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium"
          >
            Integrations
          </Link>
        </nav>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
        <p className="mt-2 text-sm text-gray-700">
          Configure connections to external systems. API credentials are stored securely in Azure Key Vault.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className={`bg-white shadow rounded-lg p-6 border-2 ${
              integration.isEnabled ? 'border-primary-100' : 'border-transparent'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{getIntegrationIcon(integration.id)}</span>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{integration.displayName}</h3>
                  {getStatusBadge(integration)}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={integration.isEnabled}
                  onChange={() => handleToggleEnabled(integration.id, integration.isEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <p className="mt-3 text-sm text-gray-500">{integration.description}</p>

            {integration.isEnabled && (
              <>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Sync frequency</dt>
                      <dd className="text-gray-900">Every {integration.syncFrequencyHours}h</dd>
                    </div>
                    {integration.lastSyncAt && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Last sync</dt>
                        <dd className="text-gray-900">
                          {new Date(integration.lastSyncAt).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {integration.lastSyncError && (
                  <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
                    {integration.lastSyncError}
                  </div>
                )}

                <div className="mt-4">
                  <button
                    onClick={() => handleTestConnection(integration.id)}
                    disabled={testingId === integration.id}
                    className="w-full inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {testingId === integration.id ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Testing...
                      </>
                    ) : (
                      'Test Connection'
                    )}
                  </button>
                </div>

                {testResult && testResult.id === integration.id && (
                  <div
                    className={`mt-3 p-2 rounded text-xs ${
                      testResult.success
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {testResult.message}
                  </div>
                )}
              </>
            )}

            {!integration.isEnabled && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-400">
                  Enable this integration to start syncing data.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-blue-800">Setting up integrations</h3>
        <p className="mt-2 text-sm text-blue-700">
          To configure an integration, you'll need to add API credentials to Azure Key Vault:
        </p>
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
          <li>Partner Center: Requires App ID, Secret, and Refresh Token</li>
          <li>NinjaOne: Requires Client ID and Client Secret</li>
          <li>Huntress: Requires API Key</li>
          <li>QuickBooks: Requires OAuth credentials</li>
          <li>ScreenConnect: Requires API Key or credentials</li>
        </ul>
      </div>
    </div>
  )
}
